import classNames from 'classnames';
import { map, intersection, concat } from 'ramda';
import PropTypes from 'prop-types';
import { Component } from 'react';
import CheckboxEditor from '../checkbox-editor/CheckboxEditor.jsx';
import OSMHoursEditor from '../osm-hours-editor/OSMHoursEditor.jsx';
import SelectFieldEditor from '../select-field-editor/SelectFieldEditor.jsx';
import TextAreaEditor from '../text-area-editor/TextAreaEditor.jsx';
import TextFieldEditor from '../text-field-editor/TextFieldEditor.jsx';
import { POI_FIELDS } from '../constants';

import s from './InfoEditor.module.scss';

const isRestricted = poi => (poi.security ? poi.security.toLowerCase() === 'inside' : false);

const makeSelectLabels = stringArray => map(item => ({ label: item, value: item }), stringArray);

const isValidUrl = value => {
    if (!value) return true; // allow empty
    try {
        const u = new URL(value);
        return u.protocol === 'https:';
    } catch {
        return false;
    }
};
const urlErrorMessage = 'Please enter a valid URL with https.';

export default class InfoEditor extends Component {
    state = {
        invalidUrl: false,
        invalidParkingUrl: false,
    };
    static propTypes = {
        changedFields: PropTypes.arrayOf(PropTypes.string),
        disabled: PropTypes.bool,
        onUpdatePoiFields: PropTypes.func,
        poi: PropTypes.object,
        terminals: PropTypes.arrayOf(PropTypes.string),
        terminalsToLevels: PropTypes.object,
    };

    static defaultProps = {
        changedFields: [],
        disabled: false,
        onUpdatePoiFields: () => {},
        poi: {},
        terminals: [''],
        terminalsToLevels: {},
    };

    setSecurityRestricted = restricted => {
        const security = restricted ? 'Inside' : 'Outside';
        this.props.onUpdatePoiFields({ security });
    };

    renderBanner = (title, fields = [], subtitle = '') => {
        const { changedFields } = this.props;
        const isDirty = intersection(fields, changedFields).length > 0;
        const bannerClasses = classNames(s.banner, { [s.dirty]: isDirty });
        const subBanner = subtitle ? <div className={s.subBanner}>{subtitle}</div> : null;
        return (
            <div className={s.bannerWrapper}>
                <div className={bannerClasses}>{title}</div>
                {subBanner}
            </div>
        );
    };

    onPhoneNumberChange = phone => {
        phone = phone?.trim() === '' ? null : phone.startsWith('+') ? phone : concat('+', phone);
        this.props.onUpdatePoiFields({ phone });
    };

    render = () => {
        const { poi = {}, onUpdatePoiFields, terminals = [], terminalsToLevels = {}, disabled } = this.props;
        const levels = terminalsToLevels[poi.terminal] || [];

        return (
            <div className={classNames(s.infoEditor)} data-testid={'info-editor'}>
                {this.renderBanner('LOCATION', [POI_FIELDS.TERMINAL, POI_FIELDS.FLOOR, POI_FIELDS.GATE])}
                <div className={s.controlsGrid}>
                    <SelectFieldEditor
                        label="Building"
                        selectedValue={poi.terminal}
                        creatable
                        options={makeSelectLabels(terminals)}
                        onValueSelected={terminal => {
                            onUpdatePoiFields({ terminal });
                        }}
                        disabled={true} // always disable building
                    />
                    <SelectFieldEditor
                        label="Level"
                        selectedValue={poi.floor}
                        creatable
                        options={makeSelectLabels(levels)}
                        onValueSelected={floor => {
                            onUpdatePoiFields({ floor });
                        }}
                        disabled={true} // always disable level
                    />
                    <TextFieldEditor
                        label="Nearby location"
                        value={poi.gate}
                        onChange={gate => {
                            onUpdatePoiFields({ gate });
                        }}
                        disabled={disabled}
                    />
                </div>

                {this.renderBanner('SECURITY RESTRICTIONS', [POI_FIELDS.SECURITY])}
                <div className={s.controlsGrid}>
                    <CheckboxEditor label="Restricted access required" value={isRestricted(poi)} onValueChanged={this.setSecurityRestricted} disabled={disabled} />
                </div>

                {this.renderBanner('DESCRIPTION', [POI_FIELDS.DESCRIPTION])}
                <TextAreaEditor
                    placeHolder="Enter a description..."
                    value={poi.description}
                    onChange={description => {
                        onUpdatePoiFields({ description });
                    }}
                    disabled={disabled}
                />

                {this.renderBanner('INFORMATION URL', [POI_FIELDS.URL], 'Use fully qualified URLs. This link will be displayed under the information section of the POI.')}
                <div className={s.controlsGrid}>
                    <div className={s.fullWidth}>
                        <TextFieldEditor
                            className={s.fullWidth}
                            value={poi.url}
                            label="URL"
                            placeHolder="https://www.example.com"
                            onChange={url => {
                                const ok = isValidUrl(url);
                                this.setState({ invalidUrl: !ok });
                                // propagate invalid flag to parent so Save can be disabled
                                onUpdatePoiFields({ hasInvalidUrl: !ok });
                                if (!ok) return;
                                onUpdatePoiFields({ url });
                            }}
                            disabled={disabled}
                        />
                        {this.state.invalidUrl && (
                            <div className={s.errorText}>{urlErrorMessage}</div>
                        )}
                    </div>
                </div>

                {poi.category?.toLowerCase().includes('parking') && (
                    <>
                        {this.renderBanner('RESERVE PARKING URL', [POI_FIELDS.PARKING_URL], 'Use fully qualified URLs. This link will be displayed as a "Reserve Parking" call to action button on the POI, directing users to a parking reservation page.')}
                        <div className={s.controlsGrid}>
                            <div className={s.fullWidth}>
                                <TextFieldEditor
                                    className={s.fullWidth}
                                    value={poi.parkingUrl}
                                    label="PARKING URL"
                                    placeHolder="https://www.example.com/parking"
                                    onChange={parkingUrl => {
                                        const ok = isValidUrl(parkingUrl);
                                        this.setState({ invalidParkingUrl: !ok });
                                        // propagate invalid flag to parent so Save can be disabled
                                        onUpdatePoiFields({ hasInvalidParkingUrl: !ok });
                                        if (!ok) return;
                                        onUpdatePoiFields({ parkingUrl });
                                    }}
                                    disabled={disabled}
                                />
                                {this.state.invalidParkingUrl && (
                                    <div className={s.errorText}>{urlErrorMessage}</div>
                                )}
                            </div>
                        </div>
                    </>
                )}

                {this.renderBanner('PHONE', [POI_FIELDS.PHONE], 'Include country code in phone numbers for international dialing support')}
                <div className={s.controlsGrid}>
                    <TextFieldEditor value={poi.phone} label="PHONE NUMBER" placeHolder="+1 (555) 555-5555" onChange={this.onPhoneNumberChange} disabled={disabled} />
                </div>

                {this.renderBanner('OPERATING HOURS', [POI_FIELDS.RAW_HOURS], 'Select the day(s)')}
                <OSMHoursEditor
                    configuredLinesString={poi.rawHours}
                    onChange={rawHours => {
                        onUpdatePoiFields({ rawHours });
                    }}
                    disabled={disabled}
                />

                <div className={s.spacer} />

                {this.renderBanner('ADDITIONAL ATTRIBUTES', [POI_FIELDS.ADDITIONAL_ATTRIBUTES])}
                <TextAreaEditor
                    placeHolder="Add some attributes..."
                    value={poi.additionalAttributes}
                    onChange={additionalAttributes => {
                        onUpdatePoiFields({ additionalAttributes });
                    }}
                    disabled={disabled}
                />
            </div>
        );
    };
}
